import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import FormModal from 'LuxKit/Modal/FormModal'
import Group from 'components/utils/Group'
import TextInput from 'components/Common/Form/Input/TextInput'
import TextArea from 'components/Common/Form/Input/TextArea'
import BodyText from 'LuxKit/Typography/BodyText'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { createWeddingMessage } from 'weddings/api/weddings'
import styled from 'styled-components'

const Content = styled(Group)`
  .flex-1 {
    flex: 1;
  }
`

const MESSAGE_MAX_LENGTH = 200

interface Props {
  weddingId: string
  coupleFirstName: string
  coupleSecondName: string
  onMessageSent?: () => void
}

function WeddingWriteAMessageModal(props: Props) {
  const { weddingId, coupleFirstName, coupleSecondName, onMessageSent } = props
  const intl = useIntl()
  const { resolve } = useModalElementContext()
  const givenName = useAppSelector(state => state.auth.account.givenName) ?? ''
  const surname = useAppSelector(state => state.auth.account.surname) ?? ''

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)
    const firstName = (data.get('firstName') as string).trim()
    const lastName = (data.get('lastName') as string).trim()
    const content = (data.get('message') as string).trim()

    const authorFirstName = firstName
    const authorLastName = lastName

    try {
      setIsSubmitting(true)
      setError(null)
      await createWeddingMessage(weddingId, { authorFirstName, authorLastName, content })
      onMessageSent?.()
      resolve()
    } catch {
      setError(intl.formatMessage({ defaultMessage: 'Failed to send message. Please try again.', id: 'DhaIdfiQDk2SLSKd-OlF', description: 'Error message shown when a wedding message submission fails.' }))
    } finally {
      setIsSubmitting(false)
    }
  }, [weddingId, onMessageSent, resolve, intl])

  const handleClose = useCallback(() => {
    resolve()
  }, [resolve])

  return (
    <FormModal
      title={intl.formatMessage(
        { defaultMessage: 'A message for {coupleFirstName} & {coupleSecondName}', id: 'yg7Gvi8poKoZpVy7Mgys', description: 'Title for the modal where guests write a public wedding message.' },
        { coupleFirstName, coupleSecondName },
      )}
      primaryActionText={isSubmitting ?
          intl.formatMessage({ defaultMessage: 'Sending...', id: 'Vsys7LzzrrUXwjogZsxC', description: 'Loading state for the wedding message submit button.' }) :
          intl.formatMessage({ defaultMessage: 'Send', id: 'cWmiLzgb-xdCfTmeHP5N', description: 'Primary action button for submitting a wedding message.' })}
      isPrimaryActionDisabled={isSubmitting}
      primaryActionType="submit"
      onSubmit={handleSubmit}
      onClose={handleClose}
    >
      <Content direction="vertical" gap={20}>
        <Group direction="horizontal" gap={16}>
          <TextInput
            name="firstName"
            label={intl.formatMessage({ defaultMessage: 'First name', id: '9MtmqtVMhdvDN4ldekUi', description: 'Label for first name input in the wedding message modal.' })}
            placeholder={intl.formatMessage({ defaultMessage: 'First name', id: 'vCJhX0vgPTv-3PJKJeQf', description: 'Placeholder for first name input in the wedding message modal.' })}
            defaultValue={givenName}
            required
            requiredErrorMessage={intl.formatMessage({ defaultMessage: 'First name is required', id: 'cx1hURrZYrMq3hrBBGSp', description: 'Validation message when message author first name is missing.' })}
            className="flex-1"
          />
          <TextInput
            name="lastName"
            label={intl.formatMessage({ defaultMessage: 'Last name', id: 'ycXydIZZmKZOXfxS83Zr', description: 'Label for last name input in the wedding message modal.' })}
            placeholder={intl.formatMessage({ defaultMessage: 'Last name', id: '5uQIQTJLv3QAedBDZwYV', description: 'Placeholder for last name input in the wedding message modal.' })}
            defaultValue={surname}
            className="flex-1"
            required
            requiredErrorMessage={intl.formatMessage({ defaultMessage: 'Last name is required', id: 'Jm8U9-ZwINe7B2tyRPcp', description: 'Validation message when message author last name is missing.' })}
          />
        </Group>
        <Group direction="vertical" gap={4}>
          <TextArea
            name="message"
            label={intl.formatMessage(
              { defaultMessage: 'A public note for {coupleFirstName} & {coupleSecondName}', id: 'kanU-W0_m8QzTLdCV5Pu', description: 'Label for public message textarea in the wedding message modal.' },
              { coupleFirstName, coupleSecondName },
            )}
            placeholder={intl.formatMessage({ defaultMessage: 'This will be displayed in the message board.', id: 'uPoHH5Wehdkwa4yexSwh', description: 'Placeholder explaining where a submitted wedding message appears.' })}
            maxLength={MESSAGE_MAX_LENGTH}
            rows={4}
            required
            requiredErrorMessage={intl.formatMessage({ defaultMessage: 'Message is required', id: 'od3NiM7KSxNcEFbZ2u8g', description: 'Validation message when wedding public message content is missing.' })}
            helpText={intl.formatMessage(
              { defaultMessage: 'Max. {maxLength} characters', id: 'M91WHB8OIXsNdp32724O', description: 'Help text showing maximum character count for a public wedding message.' },
              { maxLength: MESSAGE_MAX_LENGTH },
            )}
          />
        </Group>
        {error && <BodyText variant="small" colour="critical">{error}</BodyText>}
      </Content>
    </FormModal>
  )
}

export default WeddingWriteAMessageModal
